import { LocalFetchError } from "./api";
import type { ExtendedErrorResponse } from "../types";
export enum ErrorResponses {
  DuplicateEntity = "duplicateEntity",
  Error = "error",
  Exception = "exception",
  InProgress = "inProgress",
  InUse = "inUse",
  InvalidPage = "invalidPage",
  NoChange = "noChange",
  NoContent = "noContent",
  NotFound = "notFound",
  NotValid = "notValid",
  Success = "success",
  NotAuthorized = "notAuthorized",
  Accepted = "accepted",
}

function isErrorResponseKind(value: unknown, kind: ErrorResponses): boolean {
  if (!isFetchErrorResponse(value)) return false;
  return value.body?.kind?.toLowerCase() === kind.toLowerCase();
}

export function isDuplicateEntityError(
  value: unknown,
): value is LocalFetchError<ExtendedErrorResponse<ErrorResponses.DuplicateEntity>> {
  if (!isFetchErrorResponse(value)) return false;
  return isErrorResponseKind(value, ErrorResponses.DuplicateEntity);
}

export function isErrorError(value: unknown): value is LocalFetchError<ExtendedErrorResponse<ErrorResponses.Error>> {
  if (!isFetchErrorResponse(value)) return false;
  return isErrorResponseKind(value, ErrorResponses.Error);
}

export function isExceptionError(
  value: unknown,
): value is LocalFetchError<ExtendedErrorResponse<ErrorResponses.Exception>> {
  if (!isFetchErrorResponse(value)) return false;
  return isErrorResponseKind(value, ErrorResponses.Exception);
}

export function isInProgressError(
  value: unknown,
): value is LocalFetchError<ExtendedErrorResponse<ErrorResponses.InProgress>> {
  if (!isFetchErrorResponse(value)) return false;
  return isErrorResponseKind(value, ErrorResponses.InProgress);
}

export function isInUseError(value: unknown): value is LocalFetchError<ExtendedErrorResponse<ErrorResponses.InUse>> {
  if (!isFetchErrorResponse(value)) return false;
  return isErrorResponseKind(value, ErrorResponses.InUse);
}

export function isInvalidPageError(
  value: unknown,
): value is LocalFetchError<ExtendedErrorResponse<ErrorResponses.InvalidPage>> {
  if (!isFetchErrorResponse(value)) return false;
  return isErrorResponseKind(value, ErrorResponses.InvalidPage);
}

export function isNoChangeError(
  value: unknown,
): value is LocalFetchError<ExtendedErrorResponse<ErrorResponses.NoChange>> {
  if (!isFetchErrorResponse(value)) return false;
  return isErrorResponseKind(value, ErrorResponses.NoChange);
}

export function isNoContentError(
  value: unknown,
): value is LocalFetchError<ExtendedErrorResponse<ErrorResponses.NoContent>> {
  if (!isFetchErrorResponse(value)) return false;
  return isErrorResponseKind(value, ErrorResponses.NoContent);
}

export function isNotFoundError(
  value: unknown,
): value is LocalFetchError<ExtendedErrorResponse<ErrorResponses.NotFound>> {
  if (!isFetchErrorResponse(value)) return false;
  return isErrorResponseKind(value, ErrorResponses.NotFound);
}

export function isNotValidError(
  value: unknown,
): value is LocalFetchError<ExtendedErrorResponse<ErrorResponses.NotValid>> {
  if (!isFetchErrorResponse(value)) return false;
  return isErrorResponseKind(value, ErrorResponses.NotValid);
}

export function isSuccessError(
  value: unknown,
): value is LocalFetchError<ExtendedErrorResponse<ErrorResponses.Success>> {
  if (!isFetchErrorResponse(value)) return false;
  return isErrorResponseKind(value, ErrorResponses.Success);
}

export function isFetchErrorResponse(value: unknown): value is LocalFetchError<ExtendedErrorResponse> {
  if (!(value instanceof LocalFetchError)) return false;
  return isApiErrorResponse(value.body);
}

export const errorResponseSchema = $ck.z.object({
  message: $ck.z.string(),
  kind: $ck.z.enum([...Object.values(ErrorResponses), ...Object.keys(ErrorResponses)] as [string, ...string[]]),
  exception: $ck.z.string().nullable(),
});

type ApiErrorResponse = $ck.z.infer<typeof errorResponseSchema>;

export function isApiErrorResponse(value: unknown): value is ApiErrorResponse {
  const validation = errorResponseSchema.safeParse(value);
  return validation.success;
}
